import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Toolbar,
  ExcelExport,
  PdfExport,
  ColumnChooser,
} from '@syncfusion/ej2-react-grids';
import has from 'lodash/has';
import toString from 'lodash/toString';
import { getFromLocalStorage } from 'localStorageService/LocalStorage';
import { PdfTrueTypeFont } from '@syncfusion/ej2-pdf-export';
import { calibriFont } from 'constant/PdfFont';
// constants
import { localStorageKeys } from 'constant/Enums';

const WalletTransactionsDataGrid = ({ data }) => {
  const toolbarOptions = ['ExcelExport', 'PdfExport', 'CsvExport', 'ColumnChooser', 'Search'];
  const ref = useRef();
  const opsCenter = getFromLocalStorage(localStorageKeys.OPS_CENTER);

  const toolbarClick = (args) => {
    const pdfExportProperties = {
      theme: {
        caption: { font: new PdfTrueTypeFont(calibriFont, 10) },
        header: { font: new PdfTrueTypeFont(calibriFont, 12) },
        record: { font: new PdfTrueTypeFont(calibriFont, 9) },
      },
    };
    switch (args.item.text) {
      case 'PDF Export':
        ref.current.pdfExport(pdfExportProperties);
        break;
      case 'Excel Export':
        ref.current.excelExport();
        break;
      case 'CSV Export':
        ref.current.csvExport();
        break;
      default:
        break;
    }
  };

  const excelQueryCellInfo = (args) => {
    if (has(args.column, 'customExcelTemplate')) {
      args.value = toString(args.column.customExcelTemplate(args.data));
    }

    if (
      args.column.field === 'debit' ||
      args.column.field === 'credit' ||
      args.column.field === 'balance'
    ) {
      args.value = (+args.value).toFixed(opsCenter?.country?.decimalNumber ?? 2);
    }
  };

  return (
    <div className="control-pane">
      <div className="control-section">
        <GridComponent
          dataSource={data}
          allowPaging={true}
          toolbar={toolbarOptions}
          allowExcelExport={true}
          allowPdfExport={true}
          ref={ref}
          toolbarClick={toolbarClick}
          showColumnChooser={true}
          excelQueryCellInfo={excelQueryCellInfo}
          pdfQueryCellInfo={excelQueryCellInfo}
          rowHeight={60}
          allowTextWrap={true}
          pageSettings={{ pageSizes: true, pageSize: 20 }}
        >
          <ColumnsDirective>
            <ColumnDirective headerText="#" field="serial" width={60} textAlign="Center" />
            <ColumnDirective
              headerText="Customer"
              field="toUser"
              template={(rowData) => (
                <Link className="primary-color" to={`/admin/users/${rowData.userId}`}>
                  {rowData.toUser}
                </Link>
              )}
              customExcelTemplate={(rowData) => rowData.toUser}
              width={140}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="TransactionDate"
              field="transactionDateString"
              width={120}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Debit"
              field="debit"
              template={(rowData) => (
                <span>
                  {rowData.debit != null &&
                    rowData.debit.toFixed(opsCenter?.country?.decimalNumber ?? 2)}
                </span>
              )}
              customExcelTemplate={(rowData) => rowData.debit}
              width={80}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Credit"
              field="credit"
              template={(rowData) => (
                <span>
                  {rowData.credit != null &&
                    rowData.credit.toFixed(opsCenter?.country?.decimalNumber ?? 2)}
                </span>
              )}
              customExcelTemplate={(rowData) => rowData.credit}
              width={80}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="Balance"
              field="balance"
              template={(rowData) => (
                <span>
                  {rowData.balance != null &&
                    rowData.balance.toFixed(opsCenter?.country?.decimalNumber ?? 2)}
                </span>
              )}
              customExcelTemplate={(rowData) => rowData.balance}
              width={80}
              textAlign="Center"
            />
            <ColumnDirective headerText="Type" field="type" width={100} textAlign="Center" />
            <ColumnDirective headerText="OrderId" field="mapId" width={80} textAlign="Center" />
            <ColumnDirective
              headerText="PaymentMethod"
              field="paymentMethod"
              width={110}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="CreatedUser"
              field="createdUser"
              template={(rowData) => (
                <Link className="primary-color" to={`/admin/users/${rowData.createdUserId}`}>
                  {rowData.createdUser}
                </Link>
              )}
              width={100}
              textAlign="Center"
            />
            <ColumnDirective
              headerText="User Type"
              field="userType"
              width={100}
              textAlign="Center"
            />
            <ColumnDirective headerText="Remark" field="remark" width={100} textAlign="Center" />
          </ColumnsDirective>
          <Inject services={[Toolbar, ExcelExport, PdfExport, ColumnChooser, Page]} />
        </GridComponent>
      </div>
    </div>
  );
};

export default React.memo(WalletTransactionsDataGrid);
